import { EnumRegionErrorType } from '@/stores/mainStore';

const { validateWhiteList, validateSupportRegionLocale } =
  validateRegionLocale();

/**
 * 取得使用者的 IP 位址（在 Server Side 從 Header 裡面取得）
 */

const getUserIP = (): string => {
  const headers = useRequestHeaders();
  const address = headers['x-forwarded-for'] || '';

  let result: string = Array.isArray(address) ? address[0] : address;

  if (result.includes(',')) {
    result = result.split(',')[0];
  }
  if (result.includes(':')) {
    result = result.split(':')[0];
  }
  return result.trim();
};

/**
 * 取得使用者的瀏覽器語系
 */

const getBrowserLocale = (): string => {
  const headers = useRequestHeaders();
  const locale = (headers['accept-language'] || '').slice(0, 5);
  return locale;
};

/**
 * 目前網址上的語系參數
 * Global：未帶語系參數則回傳「/」，不支援的語系則回傳 null
 * ACN：未帶語系參數則回傳「/」，ZH_CN 和 EN_TW 以外的語系則回傳 null
 */

const getLocale = (
  path: string,
  localeFromRoute: string
): EnumLocale | '/' | null => {
  const runtimeConfig = useRuntimeConfig();
  const { CONFIGURATION } = runtimeConfig.public;
  const { $i18n } = useNuxtApp();

  if (path === '/') {
    return '/';
  }

  if (CONFIGURATION !== 'ACN') {
    const supportedLocales = $i18n.locales.value.map((item) =>
      typeof item !== 'string' ? item.code : item
    );
    // 檢查路由中的語系是否在支援的語系列表中
    if (!supportedLocales.includes(localeFromRoute as EnumLocale)) {
      return null;
    }
  }

  if (CONFIGURATION === 'ACN') {
    if (
      localeFromRoute !== EnumLocale.ZH_CN &&
      localeFromRoute !== EnumLocale.EN_TW
    ) {
      return null;
    }
  }
  return localeFromRoute as EnumLocale;
};

/**
 * 取得基礎路徑
 * Global：/{locale}
 * ACN：/{locale}/marketplace
 */

const getPath = (locale: string): string => {
  const runtimeConfig = useRuntimeConfig();
  const { CONFIGURATION } = runtimeConfig.public;

  if (CONFIGURATION === 'ACN') {
    return `/${locale}/marketplace`;
  }
  return `/${locale}`;
};

export default defineNuxtRouteMiddleware(async (to) => {
  const runtimeConfig = useRuntimeConfig();
  const { ENVIRONMENT, CONFIGURATION } = runtimeConfig.public;
  const { $pinia, payload } = useNuxtApp();
  const mainStore = await useMainStore($pinia);
  const profileStore = await useProfileStore($pinia);

  const {
    serverIp,
    regions,
    regionCode,
    regionData,
    browserLocale,
    matchedLocale,
    isInitialized,
  } = storeToRefs(mainStore);

  const { isUserVerifying } = storeToRefs(profileStore);
  const { query, path } = to;
  const localeFromRoute = path.split('/')[1];

  if (import.meta.server && payload.serverRendered) {
    const cookie = useCookie('REGION_LOCALE_ERROR_TYPE', { secure: true });
    const locale = getLocale(path, localeFromRoute);

    isUserVerifying.value = true;
    serverIp.value = getUserIP();
    browserLocale.value = getBrowserLocale();

    await mainStore.GET_REGIONS();
    await mainStore.GET_CHECK_LOGIN();

    const handleSetErrorCode = (errorCode: EnumRegionErrorType): void => {
      cookie.value = errorCode;
    };

    // Global 環境
    if (CONFIGURATION !== 'ACN') {
      // 根據目前網址上的語系參數判斷是否支援地區語系
      const isSupportRegionLocale: boolean = (() => {
        if (validateWhiteList(path)) {
          return true;
        }

        if (locale === null || locale === '/') {
          return false;
        }

        if (regionCode.value) {
          const regionItem = regions.value.find(
            (item) => item.regionCode === regionCode.value
          );
          if (regionItem) {
            return regionItem.websites.some((item) => item.code === locale);
          }
        }
        return false;
      })();

      if (!isSupportRegionLocale) {
        // 預設語系
        const defaultLocale = EnumLocale.EN_INT;

        // 未帶語系參數
        if (locale === '/' && regionCode.value === null) {
          return navigateTo(
            { path: getPath(defaultLocale), query },
            { redirectCode: 302 }
          );
        }
        if (locale === '/' && regionCode.value === EnumRegion.CN) {
          handleSetErrorCode(EnumRegionErrorType.A);
          return navigateTo(
            { path: getPath(defaultLocale), query },
            { redirectCode: 302 }
          );
        }
        if (locale === '/' && regionCode.value) {
          return await navigateTo(
            { path: getPath(matchedLocale.value), query },
            { redirectCode: 302 }
          );
        }

        // 語系參數不支援（相容已移除的語系）
        if (locale === null && localeFromRoute === 'en-us') {
          const redirectPath = replaceLocaleInPath(path, EnumLocale.EN_INT);
          return navigateTo(
            { path: redirectPath, query },
            { redirectCode: 301 }
          );
        }
        if (locale === null && localeFromRoute === 'ko-kr') {
          const redirectPath = replaceLocaleInPath(path, EnumLocale.EN_KR);
          return navigateTo(
            { path: redirectPath, query },
            { redirectCode: 301 }
          );
        }
        if (locale === null && localeFromRoute === 'en-eu') {
          const redirectPath = replaceLocaleInPath(path, EnumLocale.EN_EMEA);
          return navigateTo(
            { path: redirectPath, query },
            { redirectCode: 301 }
          );
        }

        // 語系參數不支援
        if (locale === null && regionCode.value === null) {
          return navigateTo(
            { path: getPath(defaultLocale), query },
            { redirectCode: 302 }
          );
        }
        if (locale === null && regionCode.value === EnumRegion.CN) {
          handleSetErrorCode(EnumRegionErrorType.A);
          return navigateTo(
            { path: getPath(defaultLocale), query },
            { redirectCode: 302 }
          );
        }
        if (locale === null && regionCode.value) {
          handleSetErrorCode(EnumRegionErrorType.B);
          return navigateTo(
            { path: getPath(matchedLocale.value), query },
            { redirectCode: 302 }
          );
        }

        // 語系參數支援但不符合地區語系
        if (locale && regionCode.value) {
          handleSetErrorCode(EnumRegionErrorType.A);
        }
      }
    }

    // ACN 環境
    if (CONFIGURATION === 'ACN') {
      // 如果目前為白名單頁面且不是 CN 語系時，自動轉址到 CN 語系
      if (locale !== EnumLocale.ZH_CN && validateWhiteList(path)) {
        const redirectPath = replaceLocaleInPath(path, EnumLocale.ZH_CN);
        return navigateTo({ path: redirectPath, query }, { redirectCode: 302 });
      }

      // 未帶語系參數
      if (locale === '/' && regionCode.value === null) {
        return navigateTo(
          { path: getPath(EnumLocale.ZH_CN), query },
          { redirectCode: 302 }
        );
      }
      if (locale === '/' && regionCode.value) {
        if (regionCode.value !== EnumRegion.CN) {
          handleSetErrorCode(EnumRegionErrorType.A);
        }
        return navigateTo(
          { path: getPath(EnumLocale.ZH_CN), query },
          { redirectCode: 302 }
        );
      }

      // 語系參數不支援
      if (locale === null && regionCode.value === null) {
        return navigateTo(
          { path: getPath(EnumLocale.ZH_CN), query },
          { redirectCode: 302 }
        );
      }
      if (locale === null && regionCode.value) {
        if (regionCode.value === EnumRegion.CN) {
          handleSetErrorCode(EnumRegionErrorType.B);
        } else {
          handleSetErrorCode(EnumRegionErrorType.A);
        }
        return navigateTo(
          { path: getPath(EnumLocale.ZH_CN), query },
          { redirectCode: 302 }
        );
      }

      // 語系參數支援但不符合地區語系
      if (locale === EnumLocale.EN_TW) {
        const ENTW_PATH = (() => {
          switch (ENVIRONMENT) {
            case 'PRODUCTION':
              return `${provideDomain(EnumLocale.EN_TW, ENVIRONMENT)}/en-tw`;
            case 'STAGING':
              return `${provideDomain(EnumLocale.EN_TW, ENVIRONMENT)}/en-tw`;
            default:
              return `${provideDomain(EnumLocale.EN_TW, ENVIRONMENT)}/en-tw`;
          }
        })();
        return navigateTo(
          { path: ENTW_PATH, query },
          { redirectCode: 302, external: true }
        );
      }
      if (locale === EnumLocale.ZH_CN && regionCode.value !== EnumRegion.CN) {
        handleSetErrorCode(EnumRegionErrorType.A);
      }
    }
  }

  if (import.meta.client) {
    const payload = {
      path,
      locale: localeFromRoute,
      regions: regions.value,
      regionCode: regionCode.value,
    };

    // 根據 regionCode 判斷目前的 locale 是否支援該區域的語系
    const isSupportRegionLocale: boolean = validateSupportRegionLocale(payload);

    // 如果使用者的地區與網站不符，則導向對應的地區語系頁面
    if (regionData.value.isLogin && !isSupportRegionLocale) {
      if (isInitialized.value === false) {
        const redirectPath = replaceLocaleInPath(path, matchedLocale.value);
        return navigateTo({ path: redirectPath, query });
      }
    }
  }
});
